import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Loader2, Save } from 'lucide-react';

const FIELD_GROUPS = [
  {
    title: 'Founder',
    fields: [
      ['founder_name', 'Founder Name'],
      ['founder_title', 'Founder Title'],
      ['founder_subtitle', 'Founder Subtitle'],
      ['founder_photo_url', 'Founder Photo URL'],
      ['founder_linkedin', 'Founder LinkedIn'],
      ['founder_facebook', 'Founder Facebook'],
      ['founder_instagram', 'Founder Instagram'],
      ['founder_tiktok', 'Founder TikTok'],
    ],
  },
  {
    title: 'Contact',
    fields: [
      ['support_email', 'Support Email'],
      ['support_whatsapp', 'WhatsApp (display format, e.g. +971 52 131 0804)'],
    ],
  },
];

const SOCIAL_FIELDS = [
  ['facebook', 'Facebook'],
  ['instagram', 'Instagram'],
  ['tiktok', 'TikTok'],
  ['linkedin', 'LinkedIn'],
  ['facebook_group', 'Facebook Group'],
  ['whatsapp_channel', 'WhatsApp Channel'],
  ['whatsapp_group', 'WhatsApp Group (optional)'],
];

export const AdminBranding = () => {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/branding`);
      setBranding(res.data);
    } catch { toast.error('Failed to load branding config'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBranding(); }, []);

  const setField = (key, val) => setBranding(prev => ({ ...prev, [key]: val }));
  const setSocial = (key, val) => setBranding(prev => ({ ...prev, socials: { ...prev.socials, [key]: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { key, updated_at, updated_by, ...payload } = branding;
      await axios.patch(`${API}/admin/branding`, payload);
      toast.success('Branding saved');
      fetchBranding();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading || !branding) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <Card className="p-6" data-testid="admin-branding-section">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Branding / Community</h2>
        <Button size="sm" className="btn-uae-red" onClick={handleSave} disabled={saving} data-testid="branding-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
        </Button>
      </div>

      {FIELD_GROUPS.map(group => (
        <div key={group.title} className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">{group.title}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {group.fields.map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                <input value={branding[key] || ''} onChange={e => setField(key, e.target.value)}
                  className="w-full h-9 rounded border border-gray-300 px-3 text-sm"
                  data-testid={`branding-${key}-input`} />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="mb-6">
        <label className="block text-xs text-gray-500 mb-1">Founder Bio</label>
        <textarea value={branding.founder_bio || ''} rows={4} onChange={e => setField('founder_bio', e.target.value)}
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm" data-testid="branding-founder-bio-input" />
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily UAE Jobs Social / Community</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {SOCIAL_FIELDS.map(([key, label]) => (
            <div key={key}>
              <label className="block text-xs text-gray-500 mb-1">{label}</label>
              <input value={branding.socials?.[key] || ''} onChange={e => setSocial(key, e.target.value)}
                className="w-full h-9 rounded border border-gray-300 px-3 text-sm"
                data-testid={`branding-social-${key}-input`} />
            </div>
          ))}
        </div>
      </div>

      <div className="mb-2">
        <label className="block text-xs text-gray-500 mb-1">Community Impact Text (e.g. "We have helped 400+...")</label>
        <input value={branding.community_impact_text || ''} onChange={e => setField('community_impact_text', e.target.value)}
          className="w-full h-9 rounded border border-gray-300 px-3 text-sm" data-testid="branding-impact-text-input" />
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Footer Disclaimer</label>
        <textarea value={branding.footer_disclaimer || ''} rows={2} onChange={e => setField('footer_disclaimer', e.target.value)}
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm" data-testid="branding-footer-disclaimer-input" />
      </div>
    </Card>
  );
};
